import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9c5cf9e4-7ffa-44c2-89bf-7aacf8535caa/artifacts/4q3hto4h_634037357_1389521753189000_3144761508300406294_n.jpg';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.username.trim(), form.password);
      navigate('/dashboard');
    } catch (err) {
      setError('Usuario o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Left side - form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Logo" className="w-20 h-20 rounded-full shadow-md mb-4 object-cover" />
            <h1 className="text-2xl font-black text-[#1a3a5c] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              FRAP Digital
            </h1>
            <p className="text-slate-500 text-sm mt-1">Cuerpo de Rescate de Ensenada, A.C.</p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
            <h2 className="text-lg font-black text-[#1a3a5c] mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Acceso al Sistema
            </h2>
            <p className="text-slate-500 text-xs mb-6 uppercase tracking-wider">Ingresa tus credenciales de unidad</p>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded mb-4" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-[#1a3a5c] uppercase tracking-wider mb-1.5">
                  Usuario
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                  placeholder="Ingresa tu usuario"
                  className="w-full border-0 border-b-2 border-slate-300 bg-transparent focus:border-[#1a7a4a] focus:outline-none text-sm py-2 px-0 font-medium text-slate-900 placeholder:text-slate-400 transition-colors"
                  required
                  data-testid="login-username-input"
                />
              </div>

              <div className="relative">
                <label className="block text-xs font-bold text-[#1a3a5c] uppercase tracking-wider mb-1.5">
                  Contraseña
                </label>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••"
                  className="w-full border-0 border-b-2 border-slate-300 bg-transparent focus:border-[#1a7a4a] focus:outline-none text-sm py-2 px-0 pr-8 font-medium text-slate-900 placeholder:text-slate-400 transition-colors"
                  required
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-0 bottom-2 text-slate-400 hover:text-slate-600" data-testid="toggle-password-btn">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1a7a4a] hover:bg-[#145d38] disabled:opacity-60 text-white font-black uppercase tracking-wider py-3 rounded-md transition-all shadow-sm active:scale-95"
                data-testid="login-submit-btn"
              >
                {loading ? 'Verificando...' : 'Ingresar al Sistema'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Right side - info */}
      <div className="hidden lg:flex flex-1 flex-col justify-center items-center p-12 text-white" style={{ background: 'linear-gradient(135deg, #1a3a5c, #0d2818)' }}>
        <div className="max-w-md text-center">
          <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-white/20">
            <img src={LOGO_URL} alt="Logo" className="w-20 h-20 rounded-full object-cover" />
          </div>
          <h2 className="text-3xl font-black mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Sistema FRAP Digital</h2>
          <p className="text-slate-300 text-sm leading-relaxed mb-8">
            Plataforma institucional de registro médico prehospitalario del Cuerpo de Rescate de Ensenada, A.C.
          </p>
          <div className="border-t border-white/10 pt-6 space-y-4 text-left">
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 flex-shrink-0" />
              <p className="text-slate-300 text-sm">Registro oficial de atenciones prehospitalarias conforme al formato FRAP institucional.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 flex-shrink-0" />
              <p className="text-slate-300 text-sm">Acceso restringido al personal autorizado del Cuerpo de Rescate de Ensenada, A.C.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-[#4ade80] rounded-full mt-2 flex-shrink-0" />
              <p className="text-slate-300 text-sm">Generación automática de folio y exportación a PDF con validez documental.</p>
            </div>
          </div>
          <p className="text-slate-500 text-xs mt-8">Cuerpo de Rescate de Ensenada, A.C. &middot; Fundado en 1978</p>
        </div>
      </div>
    </div>
  );
}
